<template>
  <router-view />
  <Global v-if="globalHave" />
</template>
<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { pasteTextRC } from '@/utils/data';
import { getDeviceType } from '@/utils/base';

// 全局组件
import Global from '@/components/global.vue';

const globalHave = ref(false); // 是否加载全局
let lastTouchEndAt = 0; // 上次触摸结束时间

// 当前页面所属模块（按路径判定，counter / wallet 各自只加载自己的逻辑）
const pathname = window.location.pathname;
const isCounter = pathname.includes('/counter');
const isWallet = pathname.includes('/wallet');

// 退出收银台：仅 counter 场景按需加载，避免 wallet 包里混入 counter/index
let backToAccessLayer = () => {};
// 列表刷新事件：仅 wallet 场景按需加载，避免 counter 包里混入 wallet/event
let runWalletEvent = () => {};

/** 防止手势缩放页面 */
const preventGestureZoom = e => e.preventDefault();

/** 防止多指缩放页面 */
const preventMultiTouchZoom = e => {
  // 某些 iOS WebView 不稳定触发 gesture 事件，双指时做一次兜底拦截
  if (e.touches && e.touches.length > 1) {
    e.preventDefault();
  }
};

/** 防止双击放大页面 */
const preventDoubleTapZoom = e => {
  if (e.touches && e.touches.length > 0) {
    return;
  }
  // 自定义软键盘：否则会 preventDefault 掉紧随其后的 click，快速连点无法输入
  if (isSoftKeyboardArea(e.target)) {
    lastTouchEndAt = Date.now();
    return;
  }

  const now = Date.now();
  if (now - lastTouchEndAt <= 300) {
    e.preventDefault();
  }
  lastTouchEndAt = now;
};

onMounted(() => {
  // 如果路径不包含 counter、wallet、maintainer，则不加载全局
  if (!isCounter && !isWallet && !pathname.includes('/maintainer')) {
    return;
  }
  // 如果路径包含 counter/payment-loading，则不加载全局
  if (pathname.includes('/counter/payment-loading')) {
    return;
  }

  globalHave.value = true; // 加载全局

  // 如果设备类型为 Android，则监听窗口大小变化
  if (getDeviceType() === 'Android') {
    window.addEventListener('resize', () => {
      if (document.activeElement.tagName === 'INPUT') {
        window.setTimeout(() => {
          // 如果当前元素是输入框，则滚动到当前元素
          document.activeElement.scrollIntoViewIfNeeded();
        }, 100);
      }
    });
  }

  // 防止误操作，放大页面禁止缩放
  document.addEventListener('gesturestart', preventGestureZoom, { passive: false });
  document.addEventListener('gesturechange', preventGestureZoom, { passive: false });
  document.addEventListener('gestureend', preventGestureZoom, { passive: false });
  document.addEventListener('touchstart', preventMultiTouchZoom, { passive: false });
  document.addEventListener('touchmove', preventMultiTouchZoom, { passive: false });
  document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });

  if (getDeviceType() === 'iOS') {
    // 禁止页面出现异常后，可以随意拖动
    document.addEventListener(
      'touchmove',
      function (e) {
        // 安全密码软键盘：轻微位移时不拦截，避免触控序列异常
        if (isSoftKeyboardArea(e.target)) {
          return;
        }
        // 如果目标是有滚动的区域，就不阻止
        if (isScrollable(e.target)) {
          return;
        }
        e.preventDefault();
      },
      { passive: false }
    );
  }

  document.title = '';

  // 路由改变的时候，检查是否为退出收银台
  window.addEventListener('popstate', () => {
    // 如果是替换路口
    if (location.search.includes('accessToken') && location.hash === '#1') {
      // 在这里处理 hash 变化后的逻辑，比如更新页面内容或路由
      const isBack = localStorage.getItem('isBack');

      if (isBack) {
        // 浏览器操作返回的 则直接退出
        backToAccessLayer();
      }
    }
  });

  window.onhashchange = () => {
    // 如果是替换路口
    if (location.search.includes('accessToken') && location.hash === '#1') {
      // 在这里处理 hash 变化后的逻辑，比如更新页面内容或路由
      const isBack = localStorage.getItem('isBack');

      if (isBack) {
        // 浏览器操作返回的 则直接退出
        backToAccessLayer();
      }
    }
  };

  // 按模块按需加载各自逻辑（放最后，避免阻塞上面的监听绑定）
  if (isCounter) {
    import('@/utils/counter/index').then(m => {
      backToAccessLayer = m.backToAccessLayer;
    });
  } else if (isWallet) {
    import('@/utils/wallet/event').then(m => {
      runWalletEvent = m.runEvent;
    });
  }
});

onUnmounted(() => {
  document.removeEventListener('gesturestart', preventGestureZoom);
  document.removeEventListener('gesturechange', preventGestureZoom);
  document.removeEventListener('gestureend', preventGestureZoom);
  document.removeEventListener('touchstart', preventMultiTouchZoom);
  document.removeEventListener('touchmove', preventMultiTouchZoom);
  document.removeEventListener('touchend', preventDoubleTapZoom);
});

function isScrollable(el) {
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const overflowY = style['overflowY'];
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

/** 钱包支付自定义数字键盘（soft-keyboard.vue 根节点 class） */
function isSoftKeyboardArea(el) {
  while (el && el !== document.body) {
    if (el.classList && el.classList.contains('softKeyboard')) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * 原生消息
 * @param {*} data
 */
window.nativeMsg = data => {
  switch (data.type) {
    case 'toast': {
      // 弹出toast
      window.$toast({
        type: data.value === 1 ? 'success' : 'error',
        text: data.value === 1 ? '保存成功' : '保存失败',
      });
      break;
    }
    case 'paste': {
      // 粘贴文本
      pasteTextRC(data.value);
      break;
    }
    case 'listRefresh': {
      // 列表刷新（仅 wallet 注册了该事件）
      runWalletEvent('listRefresh');
      break;
    }
    default:
  }
};
</script>
